import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import VideoDemo from "@/pages/detail/components/VideoDemo";
import ShareModal from "@/pages/detail/components/ShareModal";
import { getMediaById, getRelatedMedia, typeLabels } from "@/utils/media";

export default function DetailPage() {
  const { id } = useParams();
  const media = getMediaById(id ?? "");
  const { isFavorite, toggle } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [watchNotice, setWatchNotice] = useState(false);

  const related = useMemo(() => (media ? getRelatedMedia(media, 6) : []), [media]);

  useDocumentMeta({
    title: media
      ? `${media.title} - 影视详情 - 亚洲一区二区在线`
      : "影视内容不存在或已下架 - 亚洲一区二区在线",
    description: media
      ? `${media.title}（${media.year}，${media.region}${typeLabels[media.type]}）：${media.synopsis.slice(0, 60)}… 查看导演、主演、评分与相关推荐。`
      : "未找到对应的影视内容，可能已下架。",
    keywords: media
      ? `${media.title},${media.region}${typeLabels[media.type]},${media.genres.join(",")},亚洲一区二区在线`
      : "亚洲一区二区在线",
  });

  if (!media) {
    return (
      <div className="mx-auto w-full px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-film-line"
          title="影视内容不存在或已下架"
          description="你要找的影视内容可能已被移除或链接有误，可以返回首页浏览其他精彩作品。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const favorited = isFavorite(media.id);

  const infoRows = [
    { label: "类型", value: typeLabels[media.type] },
    { label: "地区", value: media.region },
    { label: "年份", value: String(media.year) },
    { label: "时长", value: media.duration },
    { label: "导演", value: media.director },
    { label: "主演", value: media.cast.join(" / ") },
  ];

  return (
    <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[
          { label: typeLabels[media.type], to: `/category/${media.type}` },
          { label: media.title },
        ]}
      />

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <div className="lg:col-span-1">
          <div className="overflow-hidden rounded-lg bg-background-200">
            <img
              src={media.poster}
              alt={media.title}
              title={`${media.title} 高清海报 - 亚洲一区二区在线`}
              className="aspect-[2/3] w-full object-top"
            />
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
              {typeLabels[media.type]}
            </span>
            {media.isNew && (
              <span className="rounded bg-accent-500 px-2 py-0.5 text-xs font-medium text-background-50">
                最新上线
              </span>
            )}
            {media.isHot && (
              <span className="rounded bg-secondary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                人气热播
              </span>
            )}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {media.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
            <span className="flex items-center gap-1 font-semibold text-accent-600">
              <i className="ri-star-fill"></i>
              {media.rating.toFixed(1)}
              <span className="font-normal text-foreground-400">/ 10</span>
            </span>
            <span>{media.year}</span>
            <span>{media.region}</span>
            <span>{media.duration}</span>
            {media.episodeInfo && <span>{media.episodeInfo}</span>}
          </div>

          <div className="mt-5 space-y-3 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            {infoRows.map((row) => (
              <div key={row.label} className="flex gap-4 text-sm">
                <span className="w-12 shrink-0 text-foreground-400">{row.label}</span>
                <span className="text-foreground-800">{row.value}</span>
              </div>
            ))}
            <div className="flex gap-4 text-sm">
              <span className="w-12 shrink-0 text-foreground-400">标签</span>
              <span className="flex flex-wrap gap-2">
                {media.genres.map((genre) => (
                  <span
                    key={genre}
                    className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900"
                  >
                    {genre}
                  </span>
                ))}
              </span>
            </div>
          </div>

          <div className="mt-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">剧情简介</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              {media.synopsis}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setWatchNotice(true)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </button>
            <button
              type="button"
              onClick={() => toggle(media.id)}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-4 py-3 text-sm font-medium transition-colors ${
                favorited
                  ? "border-accent-300 bg-accent-50 text-accent-600"
                  : "border-background-300 text-foreground-700 hover:bg-background-100"
              }`}
            >
              <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
            >
              <i className="ri-share-forward-line"></i>
              分享
            </button>
          </div>

          {watchNotice && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-accent-700">
              <i className="ri-information-line mt-0.5"></i>
              <p>
                播放功能暂未开放，本站为演示站点，不提供真实的影视播放服务。
                你可以先收藏本作品，或查看下方相关推荐。
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <VideoDemo poster={media.poster} title={media.title} />
      </div>

      <section className="mt-10">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <i className="ri-movie-line text-primary-500"></i>
              相关推荐
            </h2>
            <p className="mt-1 text-sm text-foreground-500">
              同类 {typeLabels[media.type]} · 你可能也喜欢
            </p>
          </div>
          <Link
            to={`/category/${media.type}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700"
          >
            查看更多
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {related.map((item) => (
            <MediaCard key={item.id} media={item} showSynopsis={false} />
          ))}
        </div>
      </section>

      <ShareModal open={shareOpen} title={media.title} onClose={() => setShareOpen(false)} />
    </div>
  );
}